import { URLQuery } from "@/utils/queryUtils";
import { dateToIsoDateTimeString, dateToUtcIsoDateTimeString, toDate } from "@/utils/utils";
import { validationConstants } from "@/utils/validationUtils";
import dayjs from "dayjs";
import { CALC_ROUTE_PATHS } from "../paths";
import { CalcRecordFilterPageRequest, CalcRecordList } from "./types";

/**
 * The filter picks whole seconds, while the API filters by exact instants and does not round them.
 */
export const toCalcRecordFilterApiRequest = ({
  createdAtMin,
  createdAtMax,
  ...request
}: CalcRecordFilterPageRequest): CalcRecordFilterPageRequest => {
  const createdAtMinDate = toDate(createdAtMin)?.startOf("second");
  const createdAtMaxDate = toDate(createdAtMax)?.endOf("second");

  return {
    ...request,
    createdAtMin: createdAtMinDate && dateToIsoDateTimeString(createdAtMinDate),
    // The API rejects an upper bound in the future and no record is newer than now, so a bound reaching now stays open
    createdAtMax:
      createdAtMaxDate && createdAtMaxDate.isBefore(dayjs()) ? dateToIsoDateTimeString(createdAtMaxDate) : undefined,
  };
};

/**
 * Path to the records list with a filter that narrows it down to the given record.
 */
export const getCalcRecordFilterPath = (record: CalcRecordList): string => {
  const createdAt = dayjs(record.createdAt);

  const filter: CalcRecordFilterPageRequest = {
    // The keyword matches the user's email, name or phone and the API caps its length, so the email is preferred
    keyword: [record.user.email, record.user.name].find(
      (value) => value && value.length <= validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
    ),
    operationType: record.operationType,
    calcType: record.calcType,
    // A second of slack on both sides keeps the record matched regardless of sub-second precision
    createdAtMin: dateToUtcIsoDateTimeString(createdAt.subtract(1, "second")),
    createdAtMax: dateToUtcIsoDateTimeString(createdAt.add(1, "second")),
  };

  return `${CALC_ROUTE_PATHS.records.to}${URLQuery.searchParamsToString({ searchParams: { ...filter } })}`;
};
